/*
 * <metafield-variants>
 *
 * Colour picker for product cards whose colours are separate sibling products linked
 * through the custom.product_variants metafield, rather than native Shopify variants.
 * Markup comes from snippets/quick-buy-metafield-swatches.liquid.
 *
 * Selecting a swatch repoints the quick buy form's hidden input[name="id"] at that
 * sibling's variant, so add-to-cart adds the chosen colour without leaving the page.
 * /cart/add.js does not require the variant to belong to the rendered product.
 *
 * This is a custom element rather than inline wiring because facet filtering re-injects
 * the whole product grid via innerHTML (assets/component-facets.js), which does not run
 * inline scripts but does upgrade custom elements.
 *
 * Deliberately does NOT dispatch VARIANT_CHANGE: every listener for that event reads
 * e.target.currentVariant, which only ever describes the card's own product.
 */
if ( ! customElements.get('metafield-variants') ) {

	class MetafieldVariants extends HTMLElement {

		constructor(){
			super();
			this.zIndex = 200;
			this._onChange = this._onChange.bind(this);
		}

		connectedCallback(){
			// Resolve everything inside the owning <quick-buy>, never through
			// document.getElementById. Card ids are built from block.id + section_id +
			// forloop index (snippets/product-item.liquid), and several callers render
			// product cards without a section_id, so those ids are not reliably unique
			// across a page. A global lookup that lands on another card silently repoints
			// the wrong form: the image would still swap (that step walks the DOM locally)
			// while add-to-cart kept adding the original product.
			this.scope = this.closest('quick-buy') || document;
			this.addEventListener('change', this._onChange);
		}

		disconnectedCallback(){
			this.removeEventListener('change', this._onChange);
		}

		_onChange(e){
			const radio = e.target;
			if ( !radio || radio.type !== 'radio' || !radio.dataset.variantId ) return;

			// Repoint the form first. If that fails there is nothing safe left to do —
			// swapping the image would tell the shopper they are buying a colour the
			// form will not add.
			if ( ! this._updateVariantInput(radio) ) return;

			this._updateAddToCart(radio);
			this._updateCardPrice(radio);
			this._updateCardImage(radio);
		}

		_form(){
			return this.scope.querySelector(`#product-form-${this.dataset.id}`)
				|| this.scope.querySelector('form[action*="/cart/add"]');
		}

		// Point the quick buy form at the selected sibling's variant.
		// Returns true only when the form really carries the new variant id.
		_updateVariantInput(radio){
			const input = this._form()?.querySelector('input[name="id"]');
			if ( !input ) {
				console.warn('[metafield-variants] no input[name="id"] for', this.dataset.id, this);
				return false;
			}
			input.value = radio.dataset.variantId;
			if ( input.value !== radio.dataset.variantId ) {
				console.warn('[metafield-variants] variant id did not stick for', this.dataset.id, this);
				return false;
			}
			return true;
		}

		// Mirrors updateAddToCartText() in component-product-form.js, but the price is
		// already money-formatted server-side so no client-side formatting is needed.
		_updateAddToCart(radio){
			const form = this._form();
			if ( !form ) {
				console.warn('[metafield-variants] no form for', this.dataset.id, this);
				return;
			}

			const button = form.querySelector('[data-js-product-add-to-cart]');
			const text = form.querySelector('[data-js-product-add-to-cart-text]');
			const available = radio.dataset.available === 'true';

			if ( button ) button.classList.toggle('disabled', !available);
			if ( !text ) return;

			if ( !available ) {
				text.textContent = OrdoUtils.localeText('products_sold_out_variant');
				return;
			}

			const price = radio.dataset.price;
			// A card's own label (data-add-label, from quick-buy.liquid) is already decoded, so it goes in as text
			const addLabel = text.dataset.addLabel;
			if ( addLabel ) text.textContent = addLabel;
			else text.innerHTML = KROWN.settings.locales.products_add_to_cart_button;
			if ( price ) text.insertAdjacentHTML('beforeend', `<span class="bold-atc-price"> - ${price}</span>`);
		}

		// Write into the hidden staging block. The MutationObserver in
		// component-quick-buy.js mirrors it onto the card and handles the empty class.
		_updateCardPrice(radio){
			// Match on the attribute, not the id — quick-buy.liquid already puts
			// data-js-quick-buy-product-price on the staging block.
			const price = this.scope.querySelector('[data-js-quick-buy-product-price]');
			if ( !price ) {
				console.warn('[metafield-variants] no price block for', this.dataset.id, this);
				return;
			}

			const original = price.querySelector('[data-js-product-price-original]');
			const compare = price.querySelector('[data-js-product-price-compare]');

			if ( original ) original.innerHTML = radio.dataset.price || '';
			if ( compare ) compare.innerHTML = radio.dataset.comparePrice || '';
		}

		// Shows the selected colour's image and hides the rest.
		_updateCardImage(radio){
			const card = this.closest('[data-js-product-item]');
			const target = card?.querySelector('[data-js-product-item-image]');
			if ( !target ) return;

			const productId = radio.dataset.productId;
			// Cards that pass image_templates get the figure rendered in Liquid (quick-buy-metafield-swatches.liquid)
			const template = this.querySelector(`template[data-sibling-image="${productId}"]`);
			if ( !template && !radio.dataset.image ) return;

			let figure = target.querySelector(`[data-swatch-product-id="${productId}"]`);

			if ( !figure ) {
				figure = template ? template.content.querySelector('figure')?.cloneNode(true) : this._buildFigure(radio, target);
				if ( !figure ) return;
				figure.dataset.swatchProductId = productId;
				target.append(figure);
			}

			// Show only the selected colour's figure. z-index layering alone is not enough:
			// these images don't fully cover one another, so every previously selected
			// colour keeps showing through underneath. Direct children only, so a
			// thumbnail-gallery slider's own figures are left alone.
			target.querySelectorAll(':scope > figure').forEach(other=>{
				const active = other === figure;
				other.style.display = active ? '' : 'none';
				other.classList.toggle('product-item__image-figure--on-top', active);
			});
			figure.style.zIndex = ++this.zIndex;
		}

		// Builds a card figure like snippets/product-card-image.liquid renders, from the radio's data-image-*
		// attributes: centre-cropped to the card ratio (data-ratio on the image box) unless the card is in fit mode.
		// The sizes come from the card's own image; the box comes from the card CSS.
		_buildFigure(radio, target){
			const cardFigure = target.querySelector('figure.product-item__image-figure');
			const cardImage = cardFigure?.querySelector('img');
			if ( !cardFigure || !cardImage ) return null;

			const sourceWidth = parseInt(radio.dataset.imageWidth, 10) || 0;
			const sourceHeight = parseInt(radio.dataset.imageHeight, 10) || 0;
			const fit = cardFigure.classList.contains('product-item__image-figure--fit');
			const ratio = parseFloat(target.dataset.ratio) || 0;
			const crop = ! fit && ratio > 0 && sourceWidth > 0 && sourceHeight > 0;
			const maxWidth = crop ? Math.min(Math.floor(sourceHeight * ratio), sourceWidth, 1280) : sourceWidth;

			const urlFor = width=>{
				const url = new URL(radio.dataset.image, window.location.href);
				url.searchParams.set('width', width);
				if ( crop ) {
					url.searchParams.set('height', Math.round(width / ratio));
					url.searchParams.set('crop', 'center');
				}
				return url.href;
			};
			const srcset = [240, 360, 480, 640, 840, 1080, 1280]
				.filter(width=>maxWidth >= width)
				.map(width=>`${urlFor(width)} ${width}w`)
				.join(', ');

			const figure = document.createElement('figure');
			figure.className = 'product-item__image-figure product-item__image-figure--primary product-item__image-no-slider';
			figure.classList.toggle('product-item__image-figure--fit', fit);

			const image = document.createElement('img');
			image.alt = radio.dataset.imageAlt || '';
			if ( crop ) {
				image.width = maxWidth;
				image.height = Math.round(maxWidth / ratio);
			} else {
				if ( sourceWidth ) image.width = sourceWidth;
				if ( sourceHeight ) image.height = sourceHeight;
			}
			if ( cardImage.sizes ) image.sizes = cardImage.sizes;
			if ( srcset ) image.srcset = srcset;
			image.src = crop ? urlFor(Math.min(480, maxWidth)) : radio.dataset.image;

			figure.append(image);
			return figure;
		}

	}

	if ( typeof customElements.get('metafield-variants') == 'undefined' ) {
		customElements.define('metafield-variants', MetafieldVariants);
	}

}
